// src/products/dataroom/components/DataroomItemRenderer.js
// Extracted rendering logic for dataroom items in different view modes
import React from 'react';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { 
  ChevronRight, ChevronDown, MoreVertical, 
  Edit2, Shield, Trash2, MessageSquare, Download, Folder, FolderOpen, Bookmark as BookmarkIcon // FIX: Added BookmarkIcon
} from 'lucide-react';
import { getFileTypeIcon, getFileIconColor } from '../utils/fileIconUtils';
 
// Constants for depth limits
const MAX_RECURSION_DEPTH = 20; // Prevent infinite recursion
const MAX_VISIBLE_DEPTH = 10;   // Hide deeper levels for performance

const DataroomItemRenderer = ({
  item,
  level = 0,
  viewMode,
  index,
  indexMap,
  isExpanded,
  hasChildren,
  isEditing,
  editName,
  showActionMenu,
  userRole,
  expandedFolders,
  // Event handlers
  onToggleExpansion,
  onNavigateToFolder,
  onOpenModal,
  onSetEditName,
  onConfirmRename,
  onCancelRename,
  onSetShowActionMenu,
  onStartRename,
  onSetConfirmDelete,
  onCreateQuestion,
  // Utilities
  formatFileSize,
  // FIX: New prop for highlighting selected folder
  selectedFolderId
}) => {
  // Safety check for recursion depth
  if (level >= MAX_RECURSION_DEPTH) {
    console.warn(`Maximum recursion depth (${MAX_RECURSION_DEPTH}) reached for item:`, item.name);
    return (
      <div className="p-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded">
        <strong>Depth Limit Reached</strong>
        <p>This folder structure is too deep to display safely. Please reorganize your files.</p>
      </div>
    );
  }

  // Performance optimization: hide very deep levels
  if (level >= MAX_VISIBLE_DEPTH) {
    return (
      <div className="p-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded">
        <span className="font-medium">{item.name}</span>
        <span className="ml-2">(hidden - level {level + 1})</span>
      </div>
    );
  }
 
  // FIX: Consolidated Icon selection (using Folder icons for folders, filetype icons for files)
  // Note: Folder icon will be determined dynamically in render functions based on expansion state
  const isSelected = item.id === selectedFolderId;

  // FIX: Action menu renderer for inline or context use
  const renderActionMenu = (item, isInline = false) => {
    
    // Define the core menu items
    const menuItems = [
      !item.is_folder && { 
        key: 'download', 
        label: 'View/Download', 
        icon: Download, 
        action: (e) => { e.stopPropagation(); onOpenModal('documentViewer', item); } 
      },
      // FIX: New Bookmark action
      { 
        key: 'bookmark', 
        label: 'Bookmark Item', 
        icon: BookmarkIcon, 
        action: (e) => { 
            e.stopPropagation(); 
            onOpenModal('bookmark', item); // Pass the full item object
        } 
      },
      userRole === 'creator' && { 
        key: 'permissions', 
        label: 'Permissions', 
        icon: Shield, 
        action: (e) => { e.stopPropagation(); onOpenModal('permissionManager', { id: item.id, name: item.name, type: item.is_folder ? 'folder' : 'file' }); } 
      },
      userRole === 'creator' && { 
        key: 'rename', 
        label: 'Rename', 
        icon: Edit2, 
        action: (e) => { e.stopPropagation(); onStartRename(item); } 
      },
      { 
        key: 'ask', 
        label: 'Ask a Question', 
        icon: MessageSquare, 
        action: (e) => { e.stopPropagation(); onCreateQuestion({ moduleReferenceId: item.id, moduleReferenceName: item.name }); } 
      },
      userRole === 'creator' && { 
        key: 'delete', 
        label: 'Delete', 
        icon: Trash2, 
        action: (e) => { e.stopPropagation(); onSetConfirmDelete(item); } 
      }
    ].filter(Boolean);

    if (isInline) {
        // Render INLINE Icons (Rename, Permissions, Question, Bookmark)
        const inlineItems = menuItems.filter(i => 
            i.key === 'ask' || i.key === 'bookmark' || (userRole === 'creator' && (i.key === 'rename' || i.key === 'permissions'))
        );
        return (
            <div className="flex items-center space-x-1 ml-auto">
                {inlineItems.map(menuItem => (
                    <button
                        key={menuItem.key}
                        onClick={menuItem.action}
                        className={`p-1 text-gray-400 rounded transition-opacity ${
                           menuItem.key === 'ask' ? 'text-orange-600' : 
                           menuItem.key === 'bookmark' ? 'text-green-600' : 'text-gray-500' 
                        } opacity-0 group-hover:opacity-100 transition-opacity`}
                        title={menuItem.label}
                    >
                        <menuItem.icon className="h-4 w-4" />
                    </button>
                ))}
            </div>
        );
    }
    
    // Render the full context menu dropdown (MoreVertical)
    return (
        <div className="relative flex items-center space-x-1 ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onSetShowActionMenu(showActionMenu === item.id ? null : item.id);
                }}
                className="p-1 hover:bg-gray-200 rounded"
                title="More actions"
            >
                <MoreVertical className="h-4 w-4 text-gray-500" />
            </button>
            {showActionMenu === item.id && (
                <div className="absolute right-0 top-full z-10 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg">
                    {menuItems.map(menuItem => (
                        <button
                            key={menuItem.key}
                            onClick={(e) => { menuItem.action(e); onSetShowActionMenu(null); }}
                            className={`flex items-center w-full px-3 py-2 text-sm hover:bg-gray-50 ${menuItem.key === 'delete' ? 'text-red-600' : 'text-gray-700'}`}
                        >
                            <menuItem.icon className="h-4 w-4 mr-2" />
                            {menuItem.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
  };
 
  // Tree view renderer
  const renderTreeNode = (item, level = 0) => {
    const isExpanded = expandedFolders.has(item.id);
    const hasChildren = item.children && item.children.length > 0;
 
    return (
      <Draggable key={item.id} draggableId={item.id.toString()} index={item.index || 0}>
        {(draggableProvided) => (
          <Droppable droppableId={`folder-${item.id}`} isDropDisabled={!item.is_folder}> 
            {(droppableProvided) => (
              <div 
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                  className="select-none"
              >
                <div
                    ref={draggableProvided.innerRef}
                    {...draggableProvided.draggableProps}
                    {...draggableProvided.dragHandleProps}
                    className={`flex items-center py-1 px-2 rounded group transition-colors ${
                        isSelected ? 'bg-blue-100 border border-blue-300' : 'hover:bg-gray-50'
                    }`}
                    style={{ paddingLeft: `${level * 16 + 8}px`, ...draggableProvided.style }}
                >
                  {/* Folder toggle */}
                  {item.is_folder && (
                    <button
                      onClick={() => onToggleExpansion(item.id)}
                      className="p-1 hover:bg-gray-200 rounded mr-1"
                    >
                      {hasChildren ? (
                        isExpanded ? (
                          <ChevronDown className="h-3 w-3 text-gray-500" />
                        ) : (
                          <ChevronRight className="h-3 w-3 text-gray-500" />
                        )
                      ) : (
                        <div className="h-3 w-3" />
                      )}
                    </button>
                  )}
 
                  {/* Icon */}
                  <div className="mr-2">
                    {item.is_folder ? (
                      isExpanded ? (
                        <FolderOpen className={`h-4 w-4 ${item.is_embedded_folder ? 'text-purple-600' : 'text-blue-600'}`} />
                      ) : (
                        <Folder className={`h-4 w-4 ${item.is_embedded_folder ? 'text-purple-600' : 'text-blue-600'}`} />
                      )
                    ) : (
                      (() => {
                        const FileIcon = getFileTypeIcon(item.name, item.file_type);
                        const fileIconColor = getFileIconColor(item.name, item.file_type);
                        return <FileIcon className={`h-4 w-4 ${fileIconColor}`} />;
                      })()
                    )}
                  </div>
 
                  {/* Name */}
                  <div className="flex-1 overflow-hidden">
                    {isEditing ? ( 
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => onSetEditName(e.target.value)}
                        onBlur={() => onConfirmRename(item)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') onConfirmRename(item);
                          if (e.key === 'Escape') onCancelRename();
                        }}
                        className="w-full px-1 py-0 text-sm border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        autoFocus
                      />
                    ) : (
                      <button
                        onClick={() => {
                          if (item.is_folder) {
                            onNavigateToFolder(item.id, item.name);
                            onToggleExpansion(item.id); 
                          } else {
                            onOpenModal('documentViewer', item);
                          }
                        }}
                        className="text-left text-sm hover:text-blue-600 flex items-center"
                      >
                        <span className="mr-1 font-semibold text-gray-400 flex-shrink-0">{indexMap && indexMap.get(item.id)}.</span>
                        <span className="truncate">{item.name}</span>
                        {item.is_embedded_folder && (
                          <span className="ml-1 text-xs text-purple-600 flex-shrink-0">(embedded)</span>
                        )}
                      </button>
                    )}
                  </div>
 
                  {/* Actions (Inline) */}
                  {renderActionMenu(item, true)} 
                  
                </div>
 
                {/* Placeholder is needed inside the Droppable component */}
                {droppableProvided.placeholder} 
 
                {/* Children (recursive rendering for tree structure) */}
                {item.is_folder && isExpanded && hasChildren && level < MAX_VISIBLE_DEPTH - 1 && (
                  <div>
                    {item.children.map(child => {
                      // Additional safety check for child items
                      if (!child || !child.id) {
                        console.warn('Invalid child item detected:', child);
                        return null;
                      }

                      return (
                        <DataroomItemRenderer 
                            key={child.id}
                            item={child}
                            level={level + 1}
                            viewMode={viewMode} 
                            index={child.index || 0}
                            indexMap={indexMap}
                            isExpanded={expandedFolders.has(child.id)}
                            hasChildren={child.children && child.children.length > 0}
                            isEditing={isEditing} 
                            editName={editName}
                            showActionMenu={showActionMenu}
                            userRole={userRole}
                            expandedFolders={expandedFolders}
                            onToggleExpansion={onToggleExpansion}
                            onNavigateToFolder={onNavigateToFolder}
                            onOpenModal={onOpenModal}
                            onSetEditName={onSetEditName}
                            onConfirmRename={onConfirmRename}
                            onCancelRename={onCancelRename}
                            onSetShowActionMenu={onSetShowActionMenu}
                            onStartRename={onStartRename}
                            onSetConfirmDelete={onSetConfirmDelete}
                            onCreateQuestion={onCreateQuestion}
                            formatFileSize={formatFileSize}
                            selectedFolderId={selectedFolderId}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </Droppable>
        )}
      </Draggable>
    );
  };
 
  // Grid/List view renderer
  const renderGridItem = (item, index) => (
    <Draggable key={item.id} draggableId={item.id.toString()} index={index}>
      {(provided, snapshot) => (
        // FIX: Only folders are droppable
        <Droppable droppableId={`folder-${item.id}`} isDropDisabled={!item.is_folder}>
          {(droppableProvided) => (
            <div
              // FIX: Apply draggable and droppable refs and props to the same outer div
              ref={(el) => {
                provided.innerRef(el);
                droppableProvided.innerRef(el);
              }}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
              // FIX: Use different styles for list/grid view
              className={`p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow cursor-pointer group ${
                viewMode === 'list' ? 'flex items-center justify-between' : 'flex flex-col'
              } ${snapshot.isDragging ? 'shadow-lg' : ''}`}
              onClick={() => item.is_folder ? onNavigateToFolder(item.id, item.name) : onOpenModal('documentViewer', item)}
              {...droppableProvided.droppableProps}
            >
              
              <div className={`flex items-center ${viewMode === 'list' ? 'flex-1 min-w-0' : 'mb-2 w-full'}`}>
                {/* Icon */}
                <div className={`${viewMode === 'list' ? 'mr-3' : 'mx-auto mb-2'}`}>
                  {item.is_folder ? (
                    <Folder className={`h-8 w-8 ${item.is_embedded_folder ? 'text-purple-600' : 'text-blue-600'}`} />
                  ) : (
                    (() => {
                      const FileIcon = getFileTypeIcon(item.name, item.file_type);
                      const fileIconColor = getFileIconColor(item.name, item.file_type);
                      return <FileIcon className={`h-8 w-8 ${fileIconColor}`} />;
                    })()
                  )}
                </div>

                {/* Name & Details */}
                <div className={`${viewMode === 'list' ? 'flex-1 min-w-0' : 'text-center'}`}>
                  {isEditing ? ( 
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => onSetEditName(e.target.value)}
                      onBlur={() => onConfirmRename(item)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') onConfirmRename(item);
                        if (e.key === 'Escape') onCancelRename();
                      }}
                      className="w-full px-1 py-0 border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    // FIX: Folder name is now next to icon (not below)
                    <p className="font-medium text-gray-900 truncate">
                      <span className="mr-2 font-semibold">{indexMap && indexMap.get(item.id)}.</span>
                      {item.name}
                      {item.is_embedded_folder && (
                        <span className="ml-1 text-xs text-purple-600">(embedded)</span>
                      )}
                    </p>
                  )}
                  
                  {item.file_size && viewMode !== 'grid' && (
                    <p className="text-xs text-gray-500 mt-1">
                      {formatFileSize(item.file_size)}
                    </p>
                  )}
                </div>
              </div>

              {/* Actions/Menu (FIXED: Always show inline actions for this view) */}
              <div className={`${viewMode === 'list' ? 'ml-4' : 'w-full flex justify-end'}`}>
                {renderActionMenu(item, true)} 
              </div>
              
              {droppableProvided.placeholder}
            </div>
          )}
        </Droppable>
      )}
    </Draggable>
  );
 
  // Main render logic based on view mode
  if (viewMode === 'tree') {
    return renderTreeNode(item, level);
  } else {
    return renderGridItem(item, index);
  }
};
 
export default DataroomItemRenderer;